import { useAuth } from '../../lib/auth.js'

export interface AttachmentResult {
  readonly fileName: string
  readonly ok: boolean
  readonly message?: string
}

/**
 * Presign -> PUT -> confirm, per file, sequentially. Sequential on purpose: a manager
 * usually attaches one or two files, and parallel uploads would multiply failure modes
 * without buying any meaningful speed.
 */
export function useTaskAttachments() {
  const { api } = useAuth()

  return async function upload(taskId: string, files: readonly File[]): Promise<AttachmentResult[]> {
    const results: AttachmentResult[] = []
    for (const file of files) {
      try {
        const presigned = await api.post<{ attachmentId: string; url: string }>(
          '/api/attachments/presign',
          { taskId, fileName: file.name, contentType: file.type, sizeBytes: file.size },
        )
        const put = await fetch(presigned.url, {
          method: 'PUT',
          headers: { 'content-type': file.type },
          body: file,
        })
        if (!put.ok) throw new Error(`Upload failed (${put.status})`)
        await api.post('/api/attachments/confirm', { attachmentId: presigned.attachmentId })
        results.push({ fileName: file.name, ok: true })
      } catch (error) {
        results.push({
          fileName: file.name,
          ok: false,
          message: error instanceof Error ? error.message : 'Upload failed',
        })
      }
    }
    return results
  }
}
